import { FormEvent, useEffect, useState } from "react";
import { styled } from "styled-components";
import { ArrowRight, CalendarDays, Check, CheckCircle2, Clock3, Compass, HeartHandshake, Lightbulb, Mail, MapPin, Phone, ShieldCheck } from "lucide-react";
import { BulletList, ButtonLink, ButtonRow, Card, Container, Grid, Pill, Section, SectionHeader } from "../components/UI";
import { packages, services, site } from "../data";
import { SEO } from "../seo";

const PageHero = styled(Section)`padding-top:clamp(5rem,10vw,8rem);`;
const Form = styled.form`display:grid;gap:1rem;`;
const Label = styled.label`display:grid;gap:.45rem;font-weight:750;`;
const Input = styled.input`padding:.82rem;border:1px solid ${({theme})=>theme.colors.border};border-radius:12px;background:${({theme})=>theme.colors.canvas};color:${({theme})=>theme.colors.text};`;
const Textarea = styled.textarea`min-height:160px;padding:.82rem;border:1px solid ${({theme})=>theme.colors.border};border-radius:12px;background:${({theme})=>theme.colors.canvas};color:${({theme})=>theme.colors.text};`;
const Select = styled.select`padding:.82rem;border:1px solid ${({theme})=>theme.colors.border};border-radius:12px;background:${({theme})=>theme.colors.canvas};color:${({theme})=>theme.colors.text};`;
const Submit = styled.button`padding:.85rem 1.1rem;border:0;border-radius:999px;background:${({theme})=>theme.colors.accent};color:#04131e;font-weight:850;cursor:pointer;`;
const LogoPanel = styled(Card)`
  display: grid;
  place-items: center;
  min-height: 440px;
  overflow: hidden;
  padding: clamp(1.5rem, 4vw, 3rem);

  img {
    display: block;
    width: min(90%, 430px);
    max-width: 100%;
    height: auto;
    aspect-ratio: 1 / 1;
    object-fit: contain;
    filter: drop-shadow(0 18px 38px rgba(0, 0, 0, 0.22));
  }
`;
const ContactHero = styled(Section)`
  padding-top: clamp(4.5rem, 8vw, 7rem);
  padding-bottom: clamp(3rem, 6vw, 5rem);
  color: ${({ theme }) => theme.colors.text};
  background:
    radial-gradient(
      circle at 85% 20%,
      ${({ theme }) => theme.colors.accentSoft},
      transparent 34%
    ),
    linear-gradient(
      135deg,
      ${({ theme }) => theme.colors.canvasAlt} 0%,
      ${({ theme }) => theme.colors.canvas} 100%
    );
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  transition:
    background 0.25s ease,
    color 0.25s ease,
    border-color 0.25s ease;
`;
const ContactHeroGrid = styled.div`display:grid;grid-template-columns:1.2fr .8fr;gap:clamp(2rem,6vw,5rem);align-items:center;@media(max-width:850px){grid-template-columns:1fr;}`;
const HeroBenefits = styled.div`display:grid;grid-template-columns:repeat(3,1fr);gap:1rem;margin-top:2rem;@media(max-width:720px){grid-template-columns:1fr;}`;
const Benefit = styled.div`
  display: flex;
  gap: 0.7rem;
  align-items: flex-start;

  svg {
    color: ${({ theme }) => theme.colors.accent};
    flex: 0 0 auto;
  }

  strong,
  span {
    display: block;
  }

  strong {
    color: ${({ theme }) => theme.colors.text};
  }

  span {
    color: ${({ theme }) => theme.colors.muted};
    font-size: 0.9rem;
  }
`;
const HeroMessage = styled(Card)`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  color: ${({ theme }) => theme.colors.text};
  background: ${({ theme }) => theme.colors.surface};

  svg {
    color: ${({ theme }) => theme.colors.accent};
    flex: 0 0 auto;
  }

  p {
    color: ${({ theme }) => theme.colors.text};
    font-size: 1.18rem;
    margin: 0;
  }
`;
const ContactGrid = styled.div`display:grid;grid-template-columns:1.08fr .92fr;gap:1.25rem;align-items:start;@media(max-width:980px){grid-template-columns:1fr;}`;
const ScheduleCard = styled(Card)`padding:1rem;overflow:hidden;scroll-margin-top:100px;`;
const MessageCard = styled(Card)`padding:clamp(1.2rem,2.5vw,2rem);`;
const CardHeading = styled.div`display:flex;gap:1rem;align-items:flex-start;margin-bottom:1.25rem;h2{margin:0;font-size:1.45rem;}p{margin:.35rem 0 0;color:${({theme})=>theme.colors.muted};}`;
const IconCircle = styled.span`width:46px;height:46px;border-radius:50%;display:grid;place-items:center;flex:0 0 auto;background:${({theme})=>theme.colors.accentSoft};color:${({theme})=>theme.colors.accent};`;
const CalendlyFrame = styled.div`min-height:760px;border-radius:18px;overflow:hidden;background:#fff;`;
const Fallback = styled.p`font-size:.9rem;color:${({theme})=>theme.colors.muted};text-align:center;margin:1rem 0 .25rem;a{color:${({theme})=>theme.colors.primary};font-weight:800;}`;
const FormRow = styled.div`display:grid;grid-template-columns:1fr 1fr;gap:1rem;@media(max-width:650px){grid-template-columns:1fr;}`;
const RadioRow = styled.div`display:flex;flex-wrap:wrap;gap:1rem;margin-top:.45rem;label{display:flex;align-items:center;gap:.4rem;font-weight:600;}`;
const PrivacyNote = styled.p`display:flex;align-items:flex-start;gap:.45rem;color:${({theme})=>theme.colors.muted};font-size:.86rem;margin:0;svg{color:${({theme})=>theme.colors.accent};flex:0 0 auto;margin-top:.15rem;}`;
const ContactBar = styled(Card)`margin-top:1.25rem;display:grid;grid-template-columns:repeat(4,1fr);gap:0;padding:1.2rem;@media(max-width:900px){grid-template-columns:repeat(2,1fr);gap:1.2rem;}@media(max-width:560px){grid-template-columns:1fr;}`;
const ContactItem = styled.div`display:flex;gap:.75rem;align-items:flex-start;padding:.4rem 1rem;border-right:1px solid ${({theme})=>theme.colors.border};svg{color:${({theme})=>theme.colors.accent};flex:0 0 auto;}strong,a,span{display:block;}a{color:${({theme})=>theme.colors.accent};font-weight:800;text-decoration:none;}span{color:${({theme})=>theme.colors.muted};font-size:.86rem;}@media(max-width:900px){border-right:0;padding:.4rem;}`;
const StepList = styled.ol`list-style:none;padding:0;margin:0;display:grid;gap:.85rem;li{display:flex;align-items:flex-start;gap:.75rem;}li span{width:28px;height:28px;border-radius:50%;display:grid;place-items:center;background:${({theme})=>theme.colors.accentSoft};color:${({theme})=>theme.colors.accent};font-weight:850;flex:0 0 auto;}`;
const PackageCard = styled(Card)`
  display:flex;flex-direction:column;
  h2{font-size:1.55rem;margin:.9rem 0 .35rem;}
  p{color:${({theme})=>theme.colors.muted};}
  small{display:block;color:${({theme})=>theme.colors.muted};margin-top:auto;padding-top:1rem;}
`;

export function About() {
  return <>
    <SEO title="About TPeck Digital | Digital Support for Growing Businesses" description="TPeck Digital takes the stress out of managing websites, hosting, business email, SEO, digital marketing, AI automation, and modern business tools." path="/about"/>
    <PageHero><Container><Grid $min="350px" $gap="2.5rem">
      <div><Pill>About TPeck Digital</Pill><SectionHeader eyebrow="A practical digital partner" title="Taking the stress out of your digital presence." lead="TPeck Digital began with a straightforward mission: empower small businesses by making web design, SEO, and digital marketing easier to understand and manage. That foundation now supports a broader range of services for growing businesses." headingAs="h1"/>
      <p style={{fontSize:"1.08rem",opacity:.76}}>Today, TPeck Digital can coordinate website development and modernization, managed hosting, business email, search visibility, social media, advertising, custom AI agents, workflow automation, and business tools through one relationship.</p>
      <p style={{fontSize:"1.08rem",opacity:.76}}>The company remains committed to personal service, practical recommendations, clear communication, and solutions that fit the business instead of forcing the business to fit the technology.</p>
      <ButtonRow><ButtonLink to="/contact#schedule">{site.consultationLabel}</ButtonLink><ButtonLink to="/mission" $secondary>Mission & vision</ButtonLink></ButtonRow></div>
      <LogoPanel><img src="/tpeck-logo-transparent.png" alt="TPeck Digital compass logo"/></LogoPanel>
    </Grid></Container></PageHero>
    <Section><Container><SectionHeader eyebrow="What clients can expect" title="Modern capability with approachable service."/>
      <Grid $min="260px">
        <Card><HeartHandshake/><h2>Personal attention</h2><p>Work with a partner who takes time to understand the business and explain recommendations clearly.</p></Card>
        <Card><Compass/><h2>Clear direction</h2><p>Prioritize what will make the greatest practical difference instead of adding technology without a reason.</p></Card>
        <Card><Lightbulb/><h2>Room to grow</h2><p>Start with a website or immediate need, then add marketing, automation, and digital tools as the business develops.</p></Card>
        <Card><ShieldCheck/><h2>Dependable support</h2><p>Hosting, business email, maintenance, and ongoing support help keep the relationship useful after launch.</p></Card>
      </Grid>
    </Container></Section>
  </>;
}

export function Mission() {
  return <>
    <SEO title="Mission & Vision | TPeck Digital" description="TPeck Digital empowers growing businesses by taking the stress out of their digital presence and making modern technology practical." path="/mission"/>
    <PageHero><Container><Pill>Mission & vision</Pill><SectionHeader eyebrow="Why TPeck Digital exists" title="Help growing businesses move forward with confidence." lead="The mission and vision retain the original focus on empowering smaller businesses while reflecting the expanded digital services now available." headingAs="h1"/>
      <Grid $min="340px" $gap="2rem">
        <Card><Compass size={32}/><h2>Mission</h2><p style={{fontSize:"1.2rem"}}>To empower growing businesses by taking the stress out of their digital presence and providing practical, reliable technology services that help them compete, operate efficiently, and grow.</p></Card>
        <Card><Lightbulb size={32}/><h2>Vision</h2><p style={{fontSize:"1.2rem"}}>To be the trusted digital partner growing businesses can turn to for everything from modern websites and online marketing to smarter automation and business tools.</p></Card>
      </Grid>
    </Container></PageHero>
    <Section><Container><SectionHeader eyebrow="Values" title="How the mission shows up in the work."/>
      <Grid $min="260px">{[
        ["Clarity","Explain choices in business terms and make scope, responsibilities, and tradeoffs visible."],
        ["Practical innovation","Use modern tools, AI, and automation when they solve a real problem—not simply because they are new."],
        ["Dependability","Build and support digital services that businesses can rely on."],
        ["Partnership","Treat the website and technology relationship as part of the business, not a one-time transaction."]
      ].map(([h,p])=><Card key={h}><CheckCircle2/><h2>{h}</h2><p>{p}</p></Card>)}</Grid>
    </Container></Section>
  </>;
}

export function Pricing() {
  return <>
    <SEO title="Digital Service Packages | TPeck Digital" description="Compare Website Launch, Website Modernization, Digital Growth, and Complete Business Digital Support packages from TPeck Digital." path="/pricing"/>
    <PageHero><Container><Pill>Service packages</Pill><SectionHeader eyebrow="A clear starting structure" title="Choose the support level that fits the business today." lead="Each package is tailored after reviewing the website, content, email, marketing, integrations, and ongoing support requirements. TPeck Digital will provide a written scope and quote before work begins." headingAs="h1"/>
      <Grid $min="270px">{packages.map((item,index)=><PackageCard key={item.name}><Pill>{index===3?"Most complete":"Tailored quote"}</Pill><h2>{item.name}</h2><p>{item.audience}</p><BulletList items={item.includes}/><small>{item.note}</small><ButtonLink style={{marginTop:"1.2rem"}} to="/contact#schedule">Discuss this package</ButtonLink></PackageCard>)}</Grid>
      <Card style={{marginTop:"2rem"}}><h2>Why prices are not presented as one-size-fits-all totals</h2><p style={{opacity:.75}}>A five-page informational website, a content-heavy modernization, an advertising program, and a custom workflow do not require the same effort. The consultation and written proposal prevent hidden assumptions while still using these packages to keep the service easy to understand.</p></Card>
    </Container></PageHero>
  </>;
}

export function FAQ() {
  const faqs=[
    ["Who does TPeck Digital serve?","TPeck Digital serves growing businesses without geographic restrictions. Services can support a new business, an established company modernizing its online presence, or a team that needs ongoing digital and technology support."],
    ["What makes TPeck Digital different from a traditional web designer?","Website design remains a core service, but TPeck Digital can also coordinate hosting, business email, SEO, digital marketing, advertising, workflow automation, custom AI agents, business templates, and ongoing support."],
    ["Is business email included with hosting?","Yes. Business email is included with every managed hosting package. The number of mailboxes, storage, and migration requirements are defined in the selected plan."],
    ["Can you modernize an existing website?","Yes. TPeck Digital can review the current website, preserve useful content and search equity, redesign the experience, improve performance and accessibility, and manage the launch or migration."],
    ["Do you offer SEO and digital marketing without building a new website?","Yes. SEO, social media, advertising, content, landing pages, and campaign support can be provided for an existing website after confirming that it can support the marketing goals."],
    ["What types of AI agents can you build?","Examples include website assistants, FAQ agents, lead qualification tools, internal knowledge assistants, and task-focused agents that connect approved business information and workflows."],
    ["What can workflow automation help with?","Common opportunities include form processing, customer follow-up, notifications, approvals, document routing, data movement between tools, reminders, and repetitive administrative tasks."],
    ["Do you sell templates through the website?","The website focuses on custom template modernization and digital tools built or revamped for a specific business rather than a general online product store."],
    ["How does a project begin?","The process starts with a Digital Solutions Consultation to understand the business need, current technology, desired outcome, timing, and budget expectations. TPeck Digital then recommends a practical next step."],
    ["Will TPeck Digital provide ongoing support?","Yes. Hosting, email, website maintenance, SEO, marketing, automation, and digital support can continue after the initial project under an agreed service plan."]
  ];
  return <>
    <SEO title="Frequently Asked Questions | TPeck Digital" description="Answers about TPeck Digital website services, hosting and business email, SEO, digital marketing, AI agents, workflow automation, pricing, and support." path="/faq" schema={{"@context":"https://schema.org","@type":"FAQPage","mainEntity":faqs.map(([q,a])=>({"@type":"Question",name:q,"acceptedAnswer":{"@type":"Answer","text":a}}))}}/>
    <PageHero><Container><SectionHeader eyebrow="FAQ" title="Clear answers before the first conversation." lead="These questions cover the expanded service model while preserving the straightforward, small-business-friendly approach of the original site." headingAs="h1"/>
      {faqs.map(([q,a])=><details key={q} style={{padding:"1.2rem 0",borderTop:"1px solid rgba(150,190,215,.16)"}}><summary style={{fontWeight:850,fontSize:"1.08rem",cursor:"pointer"}}>{q}</summary><p style={{maxWidth:"72ch",opacity:.72}}>{a}</p></details>)}
    </Container></PageHero>
  </>;
}

export function Contact({ schedule=false }: { schedule?: boolean }) {
  const [status,setStatus]=useState("");

  useEffect(()=>{
    if(typeof window === "undefined") return;
    const shouldScroll = schedule || window.location.hash === "#schedule";
    if(shouldScroll){
      window.setTimeout(()=>document.getElementById("schedule")?.scrollIntoView({behavior:"smooth",block:"start"}),120);
    }
    const scriptSrc="https://assets.calendly.com/assets/external/widget.js";
    if(!document.querySelector(`script[src="${scriptSrc}"]`)){
      const script=document.createElement("script");
      script.src=scriptSrc;
      script.async=true;
      document.body.appendChild(script);
    }
  },[schedule]);

  const submit=async(e:FormEvent<HTMLFormElement>)=>{
    e.preventDefault();
    const form=e.currentTarget;
    const data=Object.fromEntries(new FormData(form).entries());
    const endpoint=import.meta.env.VITE_CONTACT_ENDPOINT;
    if(endpoint){
      try{const response=await fetch(endpoint,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify(data)});if(!response.ok)throw new Error();setStatus("Thank you. Your request has been submitted.");form.reset();return;}catch{setStatus(`The secure form is temporarily unavailable. Please email ${site.email}.`);}
    }else{
      const subject=encodeURIComponent(`TPeck Digital inquiry: ${data.interest||"Digital Solutions Consultation"}`);
      const body=encodeURIComponent(`Name: ${data.name}\nBusiness: ${data.organization}\nEmail: ${data.email}\nPhone: ${data.phone}\nPreferred contact: ${data.preferredContact}\nWebsite: ${data.website}\n\nHow can we help?\n${data.message}`);
      window.location.href=`mailto:${site.email}?subject=${subject}&body=${body}`;
      setStatus("Your email application has been opened with the inquiry details.");
    }
  };

  return <>
    <SEO title="Contact & Schedule a Consultation | TPeck Digital" description="Schedule a Digital Solutions Consultation or contact TPeck Digital about website modernization, hosting, SEO, digital marketing, AI, automation, or business tools." path="/contact"/>
<ContactHero>
  <Container>
    <ContactHeroGrid>
      <div>
        <Pill>Contact TPeck Digital</Pill>

        <SectionHeader
          eyebrow="Start with a focused conversation"
          title="Let’s discuss what your business needs next."
          lead="Whether you need a new website, a stronger digital presence, smarter marketing, workflow automation, or practical AI solutions, the first step is a focused conversation."
          headingAs="h1"
        />

        <HeroBenefits>
          <Benefit>
            <CheckCircle2 />
            <div>
              <strong>No-obligation consultation</strong>
              <span>
                A practical discussion, not a high-pressure sales call.
              </span>
            </div>
          </Benefit>

          <Benefit>
            <Clock3 />
            <div>
              <strong>30-minute call</strong>
              <span>
                Focused on your business, goals, and immediate priorities.
              </span>
            </div>
          </Benefit>

          <Benefit>
            <Lightbulb />
            <div>
              <strong>Practical next steps</strong>
              <span>
                Clear recommendations based on what the business actually
                needs.
              </span>
            </div>
          </Benefit>
        </HeroBenefits>
      </div>

      <HeroMessage>
        <HeartHandshake size={34} />
        <p>
          We help growing businesses modernize, automate, and grow with
          technology that works.
        </p>
      </HeroMessage>
    </ContactHeroGrid>
  </Container>
</ContactHero>

    <Section id="schedule">
      <Container>
        <ContactGrid>
          <ScheduleCard>
            <CardHeading><IconCircle><CalendarDays/></IconCircle><div><h2>Schedule a Digital Solutions Consultation</h2><p>Reserve a complimentary 30-minute consultation at a time that works for you.</p></div></CardHeading>
            <CalendlyFrame>
              <div
                className="calendly-inline-widget"
                data-url="https://calendly.com/tom-tpeckdigital/new-meeting?utm_source=tpeckdigital&utm_medium=website&utm_campaign=contact_page"
                style={{minWidth:"320px",height:"760px"}}
                aria-label="Schedule a Digital Solutions Consultation with TPeck Digital"
              />
            </CalendlyFrame>
            <Fallback>Having trouble with the scheduler? <a href="https://calendly.com/tom-tpeckdigital/new-meeting" target="_blank" rel="noreferrer">Open Calendly in a new window</a>.</Fallback>
          </ScheduleCard>

          <MessageCard>
            <CardHeading><IconCircle><Mail/></IconCircle><div><h2>Send us a message</h2><p>Have a question or project in mind? Share a few details and we will follow up.</p></div></CardHeading>
            <Form onSubmit={submit}>
              <FormRow>
                <Label>Full name *<Input name="name" required autoComplete="name" placeholder="Your name"/></Label>
                <Label>Business name<Input name="organization" autoComplete="organization" placeholder="Your business or organization"/></Label>
              </FormRow>
              <FormRow>
                <Label>Email address *<Input name="email" type="email" required autoComplete="email" placeholder="you@example.com"/></Label>
                <Label>Phone number<Input name="phone" type="tel" autoComplete="tel" placeholder="(609) 555-0123"/></Label>
              </FormRow>
              <Label>Website<Input name="website" type="url" inputMode="url" placeholder="https://yourwebsite.com"/></Label>
              <Label>Service of interest<Select name="interest"><option value="">Select a service</option>{services.map(s=><option key={s.slug}>{s.name}</option>)}<option>Complete Business Digital Support</option><option>General inquiry</option></Select></Label>
              <Label>How can we help? *<Textarea name="message" required placeholder="Tell us about your project, challenge, or question..."/></Label>
              <fieldset>
                <legend>Preferred contact method</legend>
                <RadioRow><label><input type="radio" name="preferredContact" value="Email" defaultChecked/> Email</label><label><input type="radio" name="preferredContact" value="Phone"/> Phone</label><label><input type="radio" name="preferredContact" value="Either"/> Either is fine</label></RadioRow>
              </fieldset>
              <Submit type="submit">Send message <ArrowRight size={17}/></Submit>
              <PrivacyNote><ShieldCheck size={16}/> We respect your privacy and use your information only to respond to your inquiry.</PrivacyNote>
              {status&&<p role="status">{status}</p>}
            </Form>
          </MessageCard>
        </ContactGrid>

        <ContactBar>
          <ContactItem><Phone/><div><strong>Call us</strong><a href={`tel:${site.phone.replace(/[^\d+]/g,"")}`}>{site.phone}</a><span>Monday–Friday, 9 a.m.–5 p.m. ET</span></div></ContactItem>
          <ContactItem><Mail/><div><strong>Email us</strong><a href={`mailto:${site.email}`}>{site.email}</a><span>Typical response within one business day</span></div></ContactItem>
          <ContactItem><MapPin/><div><strong>Serving clients</strong><span>Growing businesses without geographic limits</span></div></ContactItem>
          <ContactItem><Clock3/><div><strong>Response time</strong><span>Within one business day</span></div></ContactItem>
        </ContactBar>
      </Container>
    </Section>

    <Section>
      <Container>
        <Grid $min="340px" $gap="2rem">
          <Card><SectionHeader eyebrow="What happens next" title="A straightforward first step."/><StepList>{[
            "Choose a convenient meeting time or send a message.",
            "Share a few details about your business and goals.",
            "Receive confirmation and meeting information.",
            "Meet for a focused discussion with no obligation.",
            "Receive practical recommendations and next steps."
          ].map((item,index)=><li key={item}><span>{index+1}</span>{item}</li>)}</StepList></Card>
          <Card><SectionHeader eyebrow="What we can discuss" title="Bring the challenge—not a predetermined solution."/><BulletList items={["New website design or website modernization","Managed hosting, business email, and domain support","SEO, digital marketing, social media, and campaigns","Custom AI agents and workflow automation","Business templates, forms, trackers, and digital tools"]}/></Card>
        </Grid>
      </Container>
    </Section>
  </>;
}
